import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/Dialog"
import { Badge } from "@/components/ui/Badge"

export type PermissionCatalog = Record<string, { label: string; actions: Record<string, string> }>

type Props = {
  role: { name: string; system: boolean; permission_keys: string[] }
  catalog: PermissionCatalog
  open: boolean
  onClose: () => void
}

// Read-only view of what a role grants: catalog categories in file order,
// only the actions this role holds. System roles hold the full catalog.
export default function RolePermissionsModal({ role, catalog, open, onClose }: Props) {
  const granted = new Set(role.permission_keys)
  const groups = Object.entries(catalog)
    .map(([category, entry]) => ({
      label: entry.label,
      actions: Object.entries(entry.actions).filter(([action]) => granted.has(`${category}.${action}`)),
    }))
    .filter((g) => g.actions.length > 0)

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {role.name}
            {role.system && <Badge variant="secondary">System</Badge>}
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto pr-1">
          {groups.length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">This role has no permissions.</p>
          ) : (
            <div className="flex flex-col gap-3">
              {groups.map((g) => (
                <div key={g.label}>
                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {g.label}
                  </div>
                  <ul className="flex flex-col gap-0.5">
                    {g.actions.map(([action, description]) => (
                      <li key={action} className="text-sm text-foreground">
                        {description}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
